/**
 * Login alanı için e-posta veya TR telefon numarası doğrulama.
 * Hata mesajları: "Geçersiz giriş bilgisi", "Geçersiz email adresi", "Geçersiz telefon numarası"
 */

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

/** Türkiye cep telefonu: 5xx xxx xx xx (10 hane, 5 ile başlar) veya 0/ +90 önekli */
function normalizePhone(value: string): string {
  return value.replace(/\D/g, '');
}

function isTurkishMobileNumber(digits: string): boolean {
  if (digits.length === 10 && digits.startsWith('5')) return true;
  if (digits.length === 11 && digits.startsWith('0') && digits[1] === '5') return true;
  if (digits.length === 12 && digits.startsWith('90') && digits[2] === '5') return true;
  return false;
}

/** Girdinin e-posta gibi mi (örn. @ içeriyor) yoksa sadece rakam/boşluk/+ ile telefon mu olduğunu tahmin et */
export function looksLikeEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.includes('@')) return true;
  const digitsOnly = normalizePhone(trimmed);
  const onlyPhoneChars = /^\+?[\d\s\-().]*$/.test(trimmed);
  if (onlyPhoneChars && digitsOnly.length >= 10) return false;
  if (onlyPhoneChars && digitsOnly.length > 0) return false;
  return true;
}

export type LoginFieldError = 'empty' | 'invalid_email' | 'invalid_phone' | null;

export interface LoginFieldValidation {
  valid: boolean;
  error: LoginFieldError;
  message: string;
}

const MSG_EMPTY = 'Geçersiz giriş bilgisi';
const MSG_INVALID_EMAIL = 'Geçersiz e-posta adresi';
const MSG_INVALID_PHONE = 'Geçersiz giriş bilgisi';

/**
 * Tek bir login alanı değeri için doğrulama (e-posta veya TR telefon).
 * Boş veya geçersiz formatta "Geçersiz giriş bilgisi"; e-posta gibi görünüp @ yoksa "Geçersiz email adresi".
 */
export function validateLoginField(value: string): LoginFieldValidation {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return { valid: false, error: 'empty', message: MSG_EMPTY };
  }
  if (looksLikeEmail(trimmed)) {
    if (!EMAIL_REGEX.test(trimmed)) {
      return { valid: false, error: 'invalid_email', message: MSG_INVALID_EMAIL };
    }
    return { valid: true, error: null, message: '' };
  }
  const digits = normalizePhone(trimmed);
  if (digits.length < 10 || !isTurkishMobileNumber(digits)) {
    return { valid: false, error: 'invalid_phone', message: MSG_INVALID_PHONE };
  }
  return { valid: true, error: null, message: '' };
}

/**
 * Doğrulama kodu SMS metni (backend tarafında kullanılacak format).
 * SMS tetiklendiğinde: "Kurumsal cüzdan hesabınıza giriş için doğrulama kodunuz: XXXXXX"
 */
export const LOGIN_VERIFICATION_SMS_TEMPLATE = 'Kurumsal cüzdan hesabınıza giriş için doğrulama kodunuz: {code}';

